import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { turnstileSiteKey } from "../../util/data";
import { State, useScript } from "../../util/useScript";

const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js";

const TURNSTILE_MIN_WIDTH = 300;
const TURNSTILE_HEIGHT = 65;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact";
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const TurnstileWidgetContainer = styled.div<{ $height: number }>`
  width: 100%;
  margin-bottom: 15px;
  height: ${({ $height }) => $height}px;
  overflow: hidden;
`;

const TurnstileMount = styled.div<{
  $scale: number;
  $useFlexibleWidth: boolean;
}>`
  ${({ $scale, $useFlexibleWidth }) =>
    $useFlexibleWidth
      ? css`
          width: 100%;
        `
      : css`
          width: ${TURNSTILE_MIN_WIDTH}px;
          transform: scale(${$scale});
          transform-origin: top left;
        `}
`;

interface SignupTurnstileWidgetProps {
  onTokenChange: (token: string | null) => void;
}

export const SignupTurnstileWidget: React.ComponentType<SignupTurnstileWidgetProps> =
  React.memo(function SignupTurnstileWidget({ onTokenChange }) {
    const outerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const { state } = useScript({
      id: "cf-turnstile-script",
      src: TURNSTILE_SCRIPT_URL,
    });

    useEffect(() => {
      const outerElement = outerRef.current;
      if (outerElement == null) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        setContainerWidth(entry.contentRect.width);
      });
      observer.observe(outerElement);

      return () => {
        observer.disconnect();
      };
    }, []);

    const useFlexibleWidth =
      containerWidth != null && containerWidth >= TURNSTILE_MIN_WIDTH;
    const scale =
      containerWidth == null
        ? 1
        : Math.min(1, containerWidth / TURNSTILE_MIN_WIDTH);
    const containerHeight = TURNSTILE_HEIGHT * scale;

    useEffect(() => {
      if (
        state !== State.READY ||
        turnstileSiteKey == null ||
        containerWidth == null ||
        containerRef.current == null ||
        widgetIdRef.current != null ||
        window.turnstile == null
      ) {
        return;
      }

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: turnstileSiteKey,
        callback: (token) => {
          onTokenChange(token);
        },
        "expired-callback": () => {
          onTokenChange(null);
        },
        "error-callback": () => {
          onTokenChange(null);
        },
        theme: "dark",
        size: useFlexibleWidth ? "flexible" : "normal",
      });

      return () => {
        if (widgetIdRef.current != null && window.turnstile != null) {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      };
    }, [state, onTokenChange, containerWidth, useFlexibleWidth]);

    if (turnstileSiteKey == null) {
      return null;
    }

    return (
      <TurnstileWidgetContainer ref={outerRef} $height={containerHeight}>
        <TurnstileMount
          ref={containerRef}
          $scale={scale}
          $useFlexibleWidth={useFlexibleWidth}
        />
      </TurnstileWidgetContainer>
    );
  });
